/* eslint-disable max-lines */
import React from 'react';
import { ReportFilter, ReportFilterDropdown } from 'models/report';
import Box from 'components/Box';
import DatePicker from 'components/Input/datepicker/Datepicker';
import Dropdown from 'components/Input/dropdown/Dropdown';
import { InputContainer } from '../styles';
import TextInput from 'components/Input/TextInput';
import selectors from 'state/selectors';
import styled from 'styled-components/macro';
import { useDispatch, useSelector } from 'react-redux';
import utils from 'utils';
import { InputRange } from './InputRange/InputRange';
import MultiSelectInput from 'components/Input/MultiSelect';
import { modalActionType } from 'utils/constants';
import action from 'state/actions';
import services from 'services';
import AutoResizeTextArea from 'components/Input/AutoResizeTextArea';
import Typography from 'components/Typography/Typography';

const WrapperFilterItem = styled.div<{ isBetween?: boolean }>`
  display: flex;
  padding-bottom: 20px;
  align-items: center;
`;
const InputContainerStyled = styled(InputContainer)`
  margin-right: 22px;
  padding-bottom: 0;
  justify-content: flex-start;
  gap: 10px;
  #label {
    width: 120px;
    text-align: right;
  }
`;
const DropdownFilterWrapper = styled.div`
  min-height: 96px;
`;
interface Props {
  filters: ReportFilter[];
  selectedManualFilters: ReportFilter[];
  setSelectedManualFilters: (filters: ReportFilter[]) => void;
  removeFilters: () => void;
  visible?: boolean;
  requiredMissingValues?: { [key: string]: string };
  setRequiredMissinValues?: React.Dispatch<React.SetStateAction<{}>>;
  calculateRowsFunction?: () => void;
  totalRows?: number;
  saveFilters: () => void;
}

export default (props: Props) => {
  const {
    filters,
    setSelectedManualFilters,
    selectedManualFilters,
    removeFilters,
    visible,
    requiredMissingValues,
    setRequiredMissinValues,
    calculateRowsFunction,
    totalRows,
    saveFilters,
  } = props;

  const dispatch = useDispatch();
  const t = utils.intl.useTranslator();
  const dateFormat = useSelector(selectors.user.getUserPreference).dateFormat;
  const selectedFilterPreference = useSelector(selectors.userReportSettings.getSelectedFilterPreference);
  const optionsFilterPreference = useSelector(selectors.userReportSettings.getOptionsFilterPreference);

  const updateErrors = (filter: ReportFilter) => {
    const updateErrorObj = { ...requiredMissingValues };
    if (updateErrorObj[filter.column]) {
      delete updateErrorObj[filter.column];
      if (setRequiredMissinValues) setRequiredMissinValues(updateErrorObj);
    }
  };

  const handleFilterChange = (list: ReportFilterDropdown[], filter: ReportFilter) => {
    const newFilters = [...selectedManualFilters];
    const newItem = selectedManualFilters.find(({ column }) => column === filter.column);

    if (newItem) {
      newItem.listOfValues = list.map(({ value, label }) => ({ code: value, name: label }));
      setSelectedManualFilters(newFilters);
    }

    updateErrors(filter);
  };

  const closeMenuOnScroll = (event: any) => {
    if (event.target instanceof Document) {
      const activeElement = event.target.activeElement;
      if (activeElement?.id?.includes('react-select')) return false;
      return true;
    }
    if (event.target instanceof HTMLElement) {
      if (event.target?.className?.includes('MenuList') || event.target.parentElement?.className?.includes('menu')) {
        return false;
      }
      return true;
    }
    return true;
  };

  const changeInput = (e: any, filter: ReportFilter) => {
    const newFilters = [...selectedManualFilters];
    const newItem = selectedManualFilters.find(({ column }) => column === filter.column);
    if (newItem) {
      newItem.listOfValues = e.target.value.length ? [{ code: e.target.value as string, name: '' }] : [];
    }
    setSelectedManualFilters(newFilters);
    updateErrors(filter);
  };

  const onChangeDatePicker = (value: Date | [Date | null, Date | null] | null, filter: ReportFilter) => {
    const newFilters = [...selectedManualFilters];
    const newItem = selectedManualFilters.find(({ column }) => column === filter.column);
    if (newItem) {
      if (Array.isArray(value)) {
        const [start, end] = value;
        newItem.listOfValues = [
          start ? { code: utils.date.formatDateForDb(start), name: '' } : null,
          end ? { code: utils.date.formatDateForDb(end), name: '' } : null,
        ].filter(Boolean) as { code: string; name: string }[];
      } else {
        newItem.listOfValues = value ? [{ code: utils.date.formatDateForDb(value), name: '' }] : [];
      }
    }
    setSelectedManualFilters(newFilters);
    updateErrors(filter);
  };

  const deletePreferenceFilters = async () => {
    try {
      if (!selectedFilterPreference) return;
      await services.deleteFiltersPreference(selectedFilterPreference?.idUserReportSetting);
      const newOptions = optionsFilterPreference.filter(
        (option) => option.idUserReportSetting !== selectedFilterPreference?.idUserReportSetting,
      );
      dispatch(action.userReportSettings.setOptionsFilterPreference(newOptions));
      dispatch(action.userReportSettings.setSelectedFilterPreference(null));
      removeFilters();
      dispatch(action.modal.closeModal());
      utils.app.notify('success', t('user_report_settings_deleted'));
    } catch (e) {
      console.error(e);
    }
  };

  const openDeleteModalFilters = () => {
    dispatch(
      action.modal.openModal(modalActionType.report.DELETE_PREFERENCE_FILTER, {
        title: t('delete_confirmation_report'),
        subtitle: t('sure_delete_preference_filters'),
        func: deletePreferenceFilters,
      }),
    );
  };

  return (
    <Box
      title={t('report-select-filters')}
      hasButton={true}
      buttonsProperty={[
        {
          buttonAction: openDeleteModalFilters,
          buttonLabel: t('delete-preference-filters'),
          isButtonActive: !!selectedFilterPreference,
          buttonIcon: 'trash',
        },
        {
          buttonAction: saveFilters,
          buttonLabel: t(selectedFilterPreference ? 'update-filters' : 'save-filters'),
          isButtonActive:
            selectedManualFilters.filter((item) => item.listOfValues && item.listOfValues.length > 0).length > 0,
          buttonIcon: 'floppy',
        },
        {
          buttonAction: removeFilters,
          buttonLabel: t('report-remove-filters'),
          isButtonActive: true,
          buttonIcon: 'times',
        },
        {
          buttonAction: calculateRowsFunction,
          buttonLabel: t('calculate-rows'),
          isButtonActive: true,
          buttonIcon: 'ladder',
        },
      ]}
      visible={visible}
    >
      <div>
        {filters &&
          filters.map((filter) => {
            const isInFilter =
              (selectedManualFilters.find(({ column }) => column === filter.column)?.operationType || [''])[0] === 'IN';
            const isBetween =
              (selectedManualFilters.find(({ column }) => column === filter.column)?.operationType || [''])[0] ===
              'BETWEEN';

            const selectedFilter = selectedManualFilters.find(({ column }) => column === filter.column);
            const listOfValues = selectedFilter?.listOfValues || [];
            const convertedList = listOfValues.map((item) => ({
              value: item.code,
              label: item.name,
            }));

            return (
              <div key={filter.column} style={{ minHeight: 49 }}>
                {filter.filterType === 'selectOptions' && filter.listOfValues && (
                  <DropdownFilterWrapper>
                    <InputContainerStyled>
                      <label id="label">{t(filter.filterColumnName)}</label>
                      <MultiSelectInput
                        id={filter.column}
                        labelledBy={filter.filterColumnName}
                        value={convertedList}
                        options={filter?.listOfValues
                          ?.filter((e) => e.code !== '0')
                          .map(({ code, name }) => ({ label: name, value: code }))}
                        onChange={(list: ReportFilterDropdown[]) => {
                          handleFilterChange(list, filter);
                        }}
                        hasFeedback={Boolean(requiredMissingValues && requiredMissingValues[filter.column])}
                        feedbackMessage={requiredMissingValues && requiredMissingValues[filter.column]}
                      />
                    </InputContainerStyled>
                  </DropdownFilterWrapper>
                )}
                {(filter.filterType === 'manual' || filter.filterType === 'date') && (
                  <>
                    <WrapperFilterItem isBetween={isBetween}>
                      <InputContainerStyled>
                        <label id="label">{t(filter.filterColumnName)}</label>
                        {filter.operationType && filter.operationType.length && (
                          <Dropdown
                            id={`${filter.column}-operation`}
                            onChange={(val) => {
                              const newFilters = [...selectedManualFilters];
                              const newItem = selectedManualFilters.find(({ column }) => column === filter.column);
                              if (newItem && newItem.operationType) {
                                newItem.operationType = [val.value];
                                if (newItem.filterType === 'manual') newItem.listOfValues = [];
                              }
                              setSelectedManualFilters(newFilters);
                            }}
                            placeholder=""
                            options={filter.operationType?.map((op) => ({ label: op, value: op }))}
                            value={
                              selectedFilter &&
                              selectedFilter.operationType && {
                                label: selectedFilter.operationType[0],
                                value: selectedFilter.operationType[0],
                              }
                            }
                            defaultDropdownValue={{ label: filter.operationType[0], value: filter.operationType[0] }}
                            closeMenuOnScroll={closeMenuOnScroll}
                          />
                        )}
                      </InputContainerStyled>
                      <InputContainerStyled>
                        {filter.filterType === 'manual' &&
                          (isBetween ? (
                            <InputRange
                              filter={filter}
                              selectedManualFilters={selectedManualFilters}
                              setSelectedManualFilters={setSelectedManualFilters}
                              requiredMissingValues={requiredMissingValues}
                            />
                          ) : isInFilter ? (
                            <AutoResizeTextArea
                              value={selectedFilter?.listOfValues?.length ? selectedFilter.listOfValues[0].code : ''}
                              onChange={(e) => changeInput(e, filter)}
                              placeholder={filter.filterColumnName}
                              hasFeedBack={Boolean(requiredMissingValues && requiredMissingValues[filter.column])}
                              feedBackMessage={requiredMissingValues && requiredMissingValues[filter.column]}
                            />
                          ) : (
                            <TextInput
                              id={`filter-input-${filter.column}`}
                              value={selectedFilter?.listOfValues?.length ? selectedFilter.listOfValues[0].code : ''}
                              hasFeedback={Boolean(requiredMissingValues && requiredMissingValues[filter.column])}
                              feedbackMessage={requiredMissingValues && requiredMissingValues[filter.column]}
                              placeholder={filter.filterColumnName}
                              type={filter.typeOfValue === 'NUMERIC' ? 'number' : 'text'}
                              onChange={(e: any) => changeInput(e, filter)}
                            />
                          ))}

                        {filter.filterType === 'date' && (
                          <DatePicker
                            selected={
                              selectedFilter?.listOfValues?.length
                                ? utils.date.convertToObjectDate(selectedFilter.listOfValues[0].code)
                                : null
                            }
                            selectedTo={
                              isBetween &&
                              selectedFilter?.listOfValues?.length &&
                              selectedFilter?.listOfValues?.length > 1
                                ? utils.date.convertToObjectDate(selectedFilter.listOfValues[1].code)
                                : null
                            }
                            onChange={(value) => onChangeDatePicker(value, filter)}
                            dateFormat={dateFormat}
                            placeholderText={
                              isBetween
                                ? `${dateFormat?.toUpperCase()} - ${dateFormat?.toUpperCase()}`
                                : dateFormat?.toUpperCase()
                            }
                            maxDate={new Date(new Date().setDate(new Date().getDate()))}
                            hasFeedback={Boolean(requiredMissingValues && requiredMissingValues[filter.column])}
                            feedbackMessage={requiredMissingValues && requiredMissingValues[filter.column]}
                            isClearable
                            portal
                            isRange={isBetween}
                            showQuickRangeButtons={isBetween}
                            quickRangeType="report"
                          />
                        )}
                      </InputContainerStyled>
                    </WrapperFilterItem>
                  </>
                )}
              </div>
            );
          })}
        {totalRows !== undefined && (
          <div style={{ textAlign: 'right' }}>
            <Typography weight={'light'}>
              {t('total-elements')}:{totalRows}
            </Typography>
          </div>
        )}
      </div>
    </Box>
  );
};
